import { TableCell, alpha, TextField, Box, Typography, Tooltip } from '@mui/material';
import React, { memo } from 'react';
import { ExpandedRow } from './ExpandedRow';
import { MeasurementsDisplay } from './Measurements';
import { StyledTableRow } from './styles';
import { TakeoffRowProps } from './types';
import { TakeoffRowContextMenu } from './TakeoffRowContextMenu';
import { useAssemblyUpdating } from '../../utils/assemblyUpdateTracker';

// Memoize individual row to prevent re-rendering when other rows change
export const TakeoffRow = memo(
  ({
    assembly,
    index,
    isSelected,
    isExpanded,
    currentTakeoffAssembly,
    currentBreakouts,
    handleSetAssembly,
    toggleExpandRow,
    handleEditAssembly,
    handleEditStyles,
    handleSetShowDeleteWarning,
    hasMultiplierOverOne,
    hasQuotedItems,
    isRenaming,
    renameValue,
    handleRenameChange,
    handleRenameSubmit,
    theme,
    selectedFilter,
    onRowRef,
  }: TakeoffRowProps) => {
    const hasNoItems = assembly.itemsList?.length === 0;
    const { hasMultiplier, multiplierValue } = hasMultiplierOverOne(assembly);

    // Get breakout information from the first item (assumes all items in assembly use same breakout)
    const breakoutInfo = React.useMemo(() => {
      const firstItemBreakout = assembly.itemsList?.[0]?.breakout;
      if (!firstItemBreakout) return null;

      // Check if it's a typical breakout by looking for typicalbreakoutid
      const isTypical = !!firstItemBreakout.typicalbreakoutid;

      return {
        name: firstItemBreakout.breakoutname || 'Unknown',
        isTypical,
        multiplier: firstItemBreakout.multiplier || 1,
      };
    }, [assembly.itemsList]);

    // Direct theme color references to ensure updates
    const primaryColor = theme.palette.text.primary;
    const errorColor = theme.palette.error.main;
    const successColor = theme.palette.success.main;
    const primaryMainColor = theme.palette.primary.main;

    // Call the ref callback when the row mounts/unmounts or selection changes
    const rowRef = React.useCallback(
      (element: HTMLTableRowElement | null) => {
        if (onRowRef) {
          onRowRef(element, isSelected);
        }
      },
      [isSelected, onRowRef]
    );

    const updating = useAssemblyUpdating(assembly.assemblyid);

    return (
      <React.Fragment key={assembly.assemblyid}>
        <StyledTableRow
          ref={rowRef}
          selected={isSelected}
          onClick={() => {
            if (updating) return; // gate click while updating
            handleSetAssembly(assembly);
          }}
          aria-expanded={isExpanded}
          aria-label={`${assembly.assemblyname} assembly, ${isSelected ? 'selected' : 'not selected'}`}
          sx={{
            // Force all text to use theme color
            '& p, & span, & div': { color: `${primaryColor} !important` },
            width: '100%',
            '& .MuiTableCell-root': {
              verticalAlign: 'top',
              py: 1.5,
            },
            opacity: updating ? 0.6 : 1,
            pointerEvents: updating ? ('none' as any) : 'auto',
          }}
        >
          <TableCell
            sx={{
              width: '70%', // Make description column take more space
              color: `${primaryColor} !important`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              maxWidth: 0, // This allows the cell to expand properly
            }}
          >
            {isRenaming && currentTakeoffAssembly?.assemblyid === assembly.assemblyid ? (
              <TextField
                value={renameValue}
                onChange={handleRenameChange}
                onBlur={handleRenameSubmit}
                autoFocus
                size="small"
                fullWidth
                variant="outlined"
                InputProps={{
                  sx: {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.paper, 0.2)
                        : alpha(theme.palette.background.paper, 0.8),
                    '& .MuiInputBase-input': {
                      color: `${primaryColor} !important`,
                    },
                  },
                }}
                aria-label="Rename assembly"
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mb: 0.75,
                    alignItems: 'center',
                  }}
                >
                  {hasNoItems && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color: `${errorColor} !important`,
                        fontWeight: 500,
                        backgroundColor: alpha(errorColor, 0.1),
                        px: 0.5,
                        py: 0.25,
                        fontSize: '0.7rem',
                        border: `1px solid ${alpha(errorColor, 0.2)}`,
                        display: 'inline-flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ⚠️ No Items
                    </Typography>
                  )}
                  {hasMultiplier && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color: `${successColor} !important`,
                        fontWeight: 500,
                        backgroundColor: alpha(successColor, 0.1),
                        px: 0.5,
                        py: 0.25,
                        fontSize: '0.7rem',
                        border: `1px solid ${alpha(successColor, 0.2)}`,
                        display: 'inline-flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {multiplierValue}x
                    </Typography>
                  )}
                  {hasQuotedItems(assembly) && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color: `${primaryMainColor} !important`,
                        fontWeight: 500,
                        backgroundColor: alpha(primaryMainColor, 0.1),
                        px: 0.5,
                        py: 0.25,
                        fontSize: '0.7rem',
                        border: `1px solid ${alpha(primaryMainColor, 0.2)}`,
                        display: 'inline-flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Quoted
                    </Typography>
                  )}
                  {breakoutInfo && (
                    <Tooltip
                      title={`Breakout: ${breakoutInfo.name}${breakoutInfo.isTypical ? ' (Typical)' : ''}`}
                      arrow
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          color: breakoutInfo.isTypical
                            ? `${theme.palette.secondary.main} !important`
                            : `${theme.palette.info.main} !important`,
                          fontWeight: 500,
                          backgroundColor: breakoutInfo.isTypical
                            ? alpha(theme.palette.secondary.main, 0.1)
                            : alpha(theme.palette.info.main, 0.1),
                          px: 0.5,
                          py: 0.25,
                          fontSize: '0.7rem',
                          border: breakoutInfo.isTypical
                            ? `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
                            : `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                          display: 'inline-flex',
                          alignItems: 'center',
                          whiteSpace: 'nowrap',
                          maxWidth: '120px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {breakoutInfo.name.length > 12
                          ? `${breakoutInfo.name.substring(0, 12)}...`
                          : breakoutInfo.name}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
                <Tooltip title={assembly.assemblyname} arrow placement="top">
                  <Typography
                    variant="body2"
                    sx={{
                      color: `${primaryColor} !important`,
                      lineHeight: 1.5,
                      fontWeight: 400,
                      textAlign: 'left',
                      wordBreak: 'break-word',
                      hyphens: 'auto',
                    }}
                  >
                    {assembly.assemblyname}
                  </Typography>
                </Tooltip>
              </Box>
            )}
          </TableCell>
          <TableCell
            sx={{
              width: '25%',
              color: `${primaryColor} !important`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
              whiteSpace: 'normal',
              textAlign: 'right',
            }}
          >
            <MeasurementsDisplay
              key={`measurements-${assembly.assemblyid}-${assembly.pointcount}-${assembly.segmentlength}-${assembly.measuredarea}-${assembly.pointsList?.length || 0}`}
              assembly={{
                ...assembly,
                pointcount: assembly.pointcount,
                segmentlength: assembly.segmentlength,
                measuredarea: assembly.measuredarea,
              }}
              selectedFilter={selectedFilter}
              theme={theme}
            />
          </TableCell>
          <TableCell
            sx={{
              width: '5%',
              color: `${primaryColor} !important`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1)}`,
              whiteSpace: 'normal',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <TakeoffRowContextMenu
                assembly={assembly}
                isExpanded={isExpanded}
                onEdit={handleEditAssembly}
                onEditStyles={handleEditStyles}
                onDelete={assembly => handleSetShowDeleteWarning(assembly, true)}
                onToggleExpand={toggleExpandRow}
                theme={theme}
              />
            </Box>
          </TableCell>
        </StyledTableRow>

        {/* Expanded Row */}
        {isExpanded && (
          <ExpandedRow assembly={assembly} theme={theme} currentBreakouts={currentBreakouts} />
        )}
      </React.Fragment>
    );
  },
  (prevProps: TakeoffRowProps, nextProps: TakeoffRowProps) => {
    // Get breakout info to compare
    const prevBreakout = prevProps.assembly.itemsList?.[0]?.breakout;
    const nextBreakout = nextProps.assembly.itemsList?.[0]?.breakout;

    // Check if breakout data has changed
    const breakoutChanged =
      prevBreakout?.multiplier !== nextBreakout?.multiplier ||
      prevBreakout?.breakoutname !== nextBreakout?.breakoutname ||
      prevBreakout?.breakoutid !== nextBreakout?.breakoutid;

    // Check if measurement data has changed (critical for updates after adding points)
    const measurementChanged =
      prevProps.assembly.pointcount !== nextProps.assembly.pointcount ||
      prevProps.assembly.segmentlength !== nextProps.assembly.segmentlength ||
      prevProps.assembly.measuredarea !== nextProps.assembly.measuredarea ||
      (prevProps.assembly.pointsList?.length || 0) !== (nextProps.assembly.pointsList?.length || 0);

    // Re-render if breakout changed, measurements changed, or other important props changed
    if (breakoutChanged || measurementChanged) return false;

    return (
      prevProps.assembly.assemblyid === nextProps.assembly.assemblyid &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.isExpanded === nextProps.isExpanded &&
      prevProps.isRenaming === nextProps.isRenaming &&
      prevProps.selectedFilter === nextProps.selectedFilter
    );
  }
);
